import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import axios from 'axios';
import { Lock, Users, UserPlus, UserMinus, ArrowLeft, Shield, Eye, EyeOff, History, Trash2, Calendar } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AdminPage = ({ onBack }) => {
  const [adminPassword, setAdminPassword] = useState('');
  const [authenticated, setAuthenticated] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  // User management
  const [users, setUsers] = useState([]);
  const [newUsername, setNewUsername] = useState('');
  const [newPassword, setNewPassword] = useState('');
  
  // Login history
  const [loginHistory, setLoginHistory] = useState([]);

  const handleAdminLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const response = await axios.post(`${BACKEND_URL}/api/admin/verify`, {
        admin_password: adminPassword
      });

      if (response.data.success) {
        setAuthenticated(true);
        loadUsers();
        loadLoginHistory();
      } else {
        setError('Invalid admin password');
      }
    } catch (err) {
      setError('Authentication failed');
    } finally {
      setLoading(false);
    }
  };

  const loadUsers = async () => {
    try {
      const response = await axios.post(`${BACKEND_URL}/api/admin/users`, {
        admin_password: adminPassword
      });
      setUsers(response.data.users || []);
    } catch (err) {
      setError('Failed to load users');
    }
  };

  const loadLoginHistory = async () => {
    try {
      const response = await axios.post(`${BACKEND_URL}/api/admin/login-history`, {
        admin_password: adminPassword
      });
      setLoginHistory(response.data.history || []);
    } catch (err) {
      console.error('Failed to load login history');
    }
  };

  const handleAddUser = async (e) => {
    e.preventDefault();
    if (!newUsername || !newPassword) {
      setError('Username and password required');
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const response = await axios.post(`${BACKEND_URL}/api/admin/add-user`, {
        admin_password: adminPassword,
        username: newUsername,
        password: newPassword
      });

      if (response.data.success) {
        setSuccess(`User "${newUsername}" added successfully`);
        setNewUsername('');
        setNewPassword('');
        loadUsers();
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to add user');
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveUser = async (username) => {
    if (!window.confirm(`Remove user "${username}"?`)) return;

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const response = await axios.post(`${BACKEND_URL}/api/admin/remove-user`, {
        admin_password: adminPassword,
        username: username
      });

      if (response.data.success) {
        setSuccess(`User "${username}" removed`);
        loadUsers();
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to remove user');
    } finally {
      setLoading(false);
    }
  };

  const handleClearHistory = async () => {
    if (!window.confirm('Clear all login history?')) return;

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const response = await axios.post(`${BACKEND_URL}/api/admin/clear-history`, {
        admin_password: adminPassword
      });

      if (response.data.success) {
        setSuccess('Login history cleared');
        setLoginHistory([]);
      }
    } catch (err) {
      setError('Failed to clear history');
    } finally {
      setLoading(false);
    }
  };

  // Group login history by date
  const groupedHistory = loginHistory.reduce((groups, login) => {
    const date = login.date || 'Unknown';
    if (!groups[date]) {
      groups[date] = [];
    }
    groups[date].push(login);
    return groups;
  }, {});

  // Admin login screen
  if (!authenticated) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-black p-4">
        <div className="w-full max-w-md">
          <Card className="bg-gray-950 border-gray-800">
            <CardHeader className="text-center">
              <div className="flex justify-center mb-4">
                <div className="w-16 h-16 bg-gradient-to-br from-red-500 to-red-700 rounded-xl flex items-center justify-center">
                  <Shield className="w-8 h-8 text-white" />
                </div>
              </div>
              <CardTitle className="text-2xl text-white">Admin Panel</CardTitle>
              <p className="text-gray-500 text-sm">Manage authorized users</p>
            </CardHeader>
            <CardContent>
              {error && (
                <div className="mb-4 bg-red-900/50 border border-red-700 text-red-300 p-3 rounded-lg text-sm">
                  {error}
                </div>
              )}

              <form onSubmit={handleAdminLogin} className="space-y-4">
                <div className="relative">
                  <Input
                    type={showPassword ? "text" : "password"}
                    placeholder="Admin Password"
                    value={adminPassword}
                    onChange={(e) => setAdminPassword(e.target.value)}
                    className="bg-gray-900 border-gray-700 text-white h-12 pr-12"
                    data-testid="admin-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-300"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>

                <Button
                  type="submit"
                  className="w-full bg-red-600 hover:bg-red-700 h-12"
                  disabled={loading || !adminPassword}
                  data-testid="admin-login-button"
                >
                  {loading ? 'Verifying...' : 'Access Admin Panel'}
                </Button>
              </form>

              <Button
                onClick={onBack}
                variant="ghost"
                className="w-full mt-4 text-gray-500 hover:text-white"
              >
                <ArrowLeft className="w-4 h-4 mr-2" /> Back to Login
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // Admin dashboard
  return (
    <div className="min-h-screen bg-black p-4">
      <div className="max-w-2xl mx-auto space-y-4">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-red-600 rounded-lg flex items-center justify-center">
              <Shield className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-white">Admin Panel</h1>
              <p className="text-gray-500 text-xs">Manage user access</p>
            </div>
          </div>
          <Button
            onClick={onBack}
            variant="outline"
            className="border-gray-700 text-gray-400 hover:text-white"
          >
            <ArrowLeft className="w-4 h-4 mr-2" /> Exit
          </Button>
        </div>

        {/* Messages */}
        {error && (
          <div className="bg-red-900/50 border border-red-700 text-red-300 p-3 rounded-lg text-sm">
            {error}
          </div>
        )}
        {success && (
          <div className="bg-green-900/50 border border-green-700 text-green-300 p-3 rounded-lg text-sm">
            {success}
          </div>
        )}

        {/* Add User */}
        <Card className="bg-gray-950 border-gray-800">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg text-white flex items-center gap-2">
              <UserPlus className="w-5 h-5 text-teal-500" />
              Add New User
            </CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleAddUser} className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <Input
                  placeholder="Username"
                  value={newUsername}
                  onChange={(e) => setNewUsername(e.target.value)}
                  className="bg-gray-900 border-gray-700 text-white"
                  data-testid="new-username-input"
                />
                <Input
                  placeholder="Password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className="bg-gray-900 border-gray-700 text-white"
                  data-testid="new-password-input"
                />
              </div>
              <Button
                type="submit"
                className="w-full bg-teal-600 hover:bg-teal-700"
                disabled={loading || !newUsername || !newPassword}
                data-testid="add-user-button"
              >
                <UserPlus className="w-4 h-4 mr-2" />
                Add User
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* Users List */}
        <Card className="bg-gray-950 border-gray-800">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg text-white flex items-center gap-2">
              <Users className="w-5 h-5 text-teal-500" />
              Authorized Users ({users.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            {users.length === 0 ? (
              <p className="text-gray-500 text-center py-4">No users yet</p>
            ) : (
              <div className="space-y-2">
                {users.map((user, index) => (
                  <div
                    key={index}
                    className="flex items-center justify-between bg-gray-900 p-3 rounded-lg"
                    data-testid={`user-${user.username}`}
                  >
                    <div>
                      <div className="text-white font-medium">{user.username}</div>
                      <div className="text-gray-500 text-xs">
                        Password: {user.password_hint} • Added: {user.created}
                      </div>
                    </div>
                    <Button
                      onClick={() => handleRemoveUser(user.username)}
                      variant="ghost"
                      size="sm"
                      className="text-red-500 hover:text-red-400 hover:bg-red-900/30"
                      data-testid={`remove-${user.username}`}
                    >
                      <UserMinus className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Login History */}
        <Card className="bg-gray-950 border-gray-800">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg text-white flex items-center gap-2">
                <History className="w-5 h-5 text-teal-500" />
                Login History ({loginHistory.length})
              </CardTitle>
              {loginHistory.length > 0 && (
                <Button
                  onClick={handleClearHistory}
                  variant="ghost"
                  size="sm"
                  className="text-red-500 hover:text-red-400 hover:bg-red-900/30"
                  data-testid="clear-history-button"
                >
                  <Trash2 className="w-4 h-4 mr-1" />
                  Clear
                </Button>
              )}
            </div>
          </CardHeader>
          <CardContent>
            {loginHistory.length === 0 ? (
              <p className="text-gray-500 text-center py-4">No login history</p>
            ) : (
              <div className="space-y-4 max-h-64 overflow-y-auto">
                {Object.entries(groupedHistory).map(([date, logins]) => (
                  <div key={date}>
                    <div className="flex items-center gap-2 mb-2">
                      <Calendar className="w-3 h-3 text-gray-500" />
                      <span className="text-xs text-gray-500 font-medium">{date}</span>
                    </div>
                    <div className="space-y-1 pl-5">
                      {logins.map((login, index) => (
                        <div
                          key={index}
                          className="flex items-center justify-between text-sm"
                        >
                          <span className="text-white">{login.username}</span>
                          <span className="text-gray-600 text-xs">
                            {login.timestamp?.split(' ')[1] || ''}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Info */}
        <div className="text-center text-gray-600 text-xs">
          Users login with their username and password to access the trading bot
        </div>
      </div>
    </div>
  );
};

export default AdminPage;
